import { useCallback, useId, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import { Icon, useDismiss } from '@/design'
import { useSession } from '@/lib/access'
import { isUnauthorized } from '@/lib/api'

function initials(value: string): string {
  return value
    .split(/[\s._@-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/** Who is signed in and with which role — the role is what decides what they see. */
export function UserMenu() {
  const session = useSession()
  const client = useApiClient()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, open, close)

  /**
   * Signing out ends the session server-side, then leaves the app.
   *
   * Where it goes is the server's answer, not this component's: when the
   * identity provider has a logout endpoint, the browser is sent there so the
   * provider's own SSO session ends too and the next person on a shared machine
   * is not signed straight back in (ADR-0004). It returns to this app
   * afterwards. With no such endpoint it just reloads `/` — which is the point
   * either way, because the session lives in a cookie the app cannot read, so
   * the only honest way to show it has gone is to ask the server again, and
   * what comes back is the 401 the signed-out gate renders the sign-in page for.
   */
  const signOut = useMutation({
    mutationFn: () => client.signOut(),
    onSuccess: (providerLogoutUrl) => window.location.assign(providerLogoutUrl ?? '/'),
    // A 401 here means the session had already gone: that is the outcome the
    // button was asking for, so it leaves the same way a success does.
    onError: (error) => {
      if (isUnauthorized(error)) window.location.assign('/')
    },
  })

  if (!session.data) return null
  const { name, email, roleLabel, permissions } = session.data
  const display = name ?? email ?? 'Your account'

  return (
    <div className="ci-user" ref={ref}>
      <button
        type="button"
        className="ci-button ci-user__button"
        data-variant="ghost"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ci-user__avatar" aria-hidden="true">
          {initials(display) || '?'}
        </span>
        <span className="ci-user__names">
          <span className="ci-user__name">{display}</span>
          <span className="ci-user__role">{roleLabel}</span>
        </span>
        <Icon name="chevronDown" size={14} />
      </button>
      {open ? (
        <div id={panelId} className="ci-popover ci-user__panel">
          <dl className="ci-user__details">
            <div>
              <dt>Signed in as</dt>
              <dd>{email ?? display}</dd>
            </div>
            <div>
              <dt>Role</dt>
              <dd>{roleLabel}</dd>
            </div>
            <div>
              <dt>Permissions</dt>
              <dd>{permissions.length}</dd>
            </div>
          </dl>
          <button
            type="button"
            className="ci-button"
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
          >
            {signOut.isPending ? 'Signing out…' : 'Sign out'}
          </button>
          {signOut.isError && !isUnauthorized(signOut.error) ? (
            <p role="alert">Could not sign you out just now. Try again shortly.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
